import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getCities,
  getDistricts,
  getFinalData,
  getOccupationArea,
  getStates,
} from "../../../../../../gateway/http/requests/CredentialNetworkRequests";

import {
  CitiesResponseData,
  DistrictsResponseData,
  FinalDataResponseData,
  NetworkClinic,
  OccupationAreaResponseData,
  SelectOption,
  StatesResponseData,
} from "./networkFormTypes";

import {
  normalizeSelectedValue,
  parseOccupationAreaData,
  parseSelectData,
  selectParsePlan,
} from "./networkFormUtils";
import productsData from "../../../../definitions/ProductsData";

export const useNetworkForm = () => {
  const [planSelected, setPlanSelected] = useState("");
  const [stateSelected, setStateSelected] = useState("");
  const [citySelected, setCitySelected] = useState("");
  const [districtSelected, setDistrictSelected] = useState("");
  const [occupationAreaSelected, setOccupationAreaSelected] = useState("");

  const plans = selectParsePlan(productsData);
  const [states, setStates] = useState<SelectOption[]>([]);
  const [cities, setCities] = useState<SelectOption[]>([]);
  const [districts, setDistricts] = useState<SelectOption[]>([]);
  const [occupationArea, setOccupationArea] = useState<SelectOption[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [services, setServices] = useState<NetworkClinic[]>([]);
  const [open, setOpen] = useState(false);

  const canSearch = useMemo(() => {
    return Boolean(planSelected && stateSelected && citySelected && !isLoading);
  }, [planSelected, stateSelected, citySelected, isLoading]);

  const resetCityDependentFields = () => {
    setCitySelected("");
    setDistrictSelected("");
    setOccupationAreaSelected("");
    setCities([]);
    setDistricts([]);
    setOccupationArea([]);
  };

  const resetDistrictDependentFields = () => {
    setDistrictSelected("");
    setOccupationAreaSelected("");
    setDistricts([]);
    setOccupationArea([]);
  };

  const loadStates = useCallback(async () => {
    setIsLoading(true);
    setStates([]);
    resetCityDependentFields();

    try {
      const responseStates = await getStates();
      const responseData = responseStates?.data as
        | StatesResponseData
        | undefined;

      setStates(parseSelectData(responseData?.estados));
    } catch {
      toast.error("Não foi possível carregar os estados. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadCities = useCallback(async (state: string) => {
    setIsLoading(true);
    setStateSelected(state);
    resetCityDependentFields();

    try {
      const responseCities = await getCities(state);
      const responseData = responseCities?.data as
        | CitiesResponseData
        | undefined;

      setCities(parseSelectData(responseData?.cidades));
    } catch {
      toast.error("Não foi possível carregar as cidades. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadDistricts = useCallback(
    async (city: string, state?: string) => {
      const selectedState = state ?? stateSelected;

      if (!selectedState) {
        return;
      }

      setIsLoading(true);
      setCitySelected(city);
      resetDistrictDependentFields();

      try {
        const [responseDistricts, responseOccupation] = await Promise.all([
          getDistricts({
            state: selectedState,
            city,
          }),
          getOccupationArea(selectedState),
        ]);

        const districtsData = responseDistricts?.data as
          | DistrictsResponseData
          | undefined;

        const occupationData = responseOccupation?.data as
          | OccupationAreaResponseData
          | undefined;

        setDistricts(parseSelectData(districtsData?.bairros, true));

        setOccupationArea(
          parseOccupationAreaData(occupationData?.lista_area_atuacao)
        );
      } catch {
        toast.error(
          "Não foi possível carregar bairros ou áreas de atuação. Tente novamente."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [stateSelected]
  );

  const searchNetwork = useCallback(async () => {
    if (!canSearch) {
      return;
    }

    setIsLoading(true);

    try {
      const finalData = await getFinalData({
        plan: planSelected,
        state: stateSelected,
        city: citySelected,
        district: normalizeSelectedValue(districtSelected),
        occupationArea: normalizeSelectedValue(occupationAreaSelected),
      });

      const responseData = finalData?.data as
        | FinalDataResponseData
        | undefined;

      const servicesList = responseData?.lista_referenciados ?? [];

      if (Array.isArray(servicesList) && servicesList.length > 0) {
        setServices(servicesList);
        setOpen(true);
        return;
      }

      setServices([]);

      toast.warn(
        "Nenhum registro encontrado para a consulta, tente com outros termos!"
      );
    } catch {
      toast.error("Não foi possível realizar a busca. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  }, [
    canSearch,
    planSelected,
    stateSelected,
    citySelected,
    districtSelected,
    occupationAreaSelected,
  ]);

  useEffect(() => {
    loadStates();
  }, [loadStates]);

  return {
    planSelected,
    stateSelected,
    citySelected,
    districtSelected,
    occupationAreaSelected,

    plans,
    states,
    cities,
    districts,
    occupationArea,
    isLoading,
    canSearch,
    services,
    open,
    setOpen,
    setPlanSelected,
    loadCities,
    loadDistricts,
    setDistrictSelected,
    setOccupationAreaSelected,
    searchNetwork,
  };
};